import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRegistrationPackages, selectAllRegistrations, selectLoading } from '../../store/childRegistration/childRegistrationSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';
import { isChildRegistrationRecordFilled } from '../../utilities/registrationPacketStatus';

const ChildRegistrationListComp = ({ data, viewMode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const registrationsFromStore = useSelector(selectAllRegistrations);
  const loading = useSelector(selectLoading);
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;

  // Use data prop if provided (for profile view or when passed from parent), otherwise use store data
  // Ensure registrations is always an array
  const registrations = Array.isArray(data) ? data : (Array.isArray(registrationsFromStore) ? registrationsFromStore : []);

  useEffect(() => {
    // Only fetch if we're in list view mode (not profile) and no data prop is provided
    if (!data && viewMode === 'list') {
      dispatch(fetchRegistrationPackages({ page: 1, limit: 100, student_id: getEffectiveStudentId(profileData) }));
    }
  }, [dispatch, data, viewMode]);

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const handleEdit = (id) => {
    const registrationData = registrations.find(reg => reg.id == id);
    navigate(`/dashboard/child-registration/edit/${id}`, { state: { registrationData } });
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    return phone;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  // Helper function to get parent name from different API structures
  const getParentName = (record) => {
    // Check for new API structure (mother_name, father_name)
    if (record.mother_name) return record.mother_name;
    if (record.father_name) return record.father_name;
    
    // Check for old API structure (parent1_first_name, parent1_last_name)
    if (record.parent1_first_name || record.parent1_last_name) {
      return `${record.parent1_first_name || ''} ${record.parent1_last_name || ''}`.trim();
    }
    
    return 'N/A';
  };

  // Helper function to get parent phone from different API structures
  const getParentPhone = (record) => {
    // Check for new API structure
    if (record.mother_home_phone) return record.mother_home_phone;
    if (record.father_home_phone) return record.father_home_phone;
    
    // Check for old API structure
    if (record.parent1_phone) return record.parent1_phone;
    
    return 'N/A';
  };

  const getChildProfileImage = (record) => {
    return (
      record?.user_details?.user_setting?.photo ||
      record?.user_setting?.photo ||
      record?.photo ||
      ''
    );
  };

  if (loading && !data) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!registrations || registrations.length === 0) {
    return (
      <div className="alert alert-info">
        No child registration records found.
      </div>
    );
  }

  return (
    <>
      <style>{`
        .registration-card {
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          background: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          margin-bottom: 20px;
        }
        .registration-card-header {
          background: #f8f9fa;
          border-bottom: 1px solid #6b7280;
          padding: 20px 24px;
        }
        .child-name-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .child-avatar {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          background: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }
        .child-avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .child-name {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }
        .registration-meta {
          display: flex;
          gap: 12px;
          margin-top: 8px;
          flex-wrap: wrap;
        }
        .meta-badge {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .meta-badge.active {
          border-color: #10b981;
          color: #10b981;
          background: #f0fdf4;
        }
        .meta-badge.inactive {
          border-color: #ef4444;
          color: #ef4444;
          background: #fef2f2;
        }
        .registration-info-section {
          padding: 18px;
          background: #f9fafb;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
        }
        .info-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }
        .info-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .info-title {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
          margin: 0;
        }
        .info-row {
          display: flex;
          padding: 4px 0;
          font-size: 0.9rem;
        }
        .info-label {
          color: #6b7280;
          min-width: 120px;
        }
        .info-value {
          color: #1f2937;
          font-weight: 500;
        }
        .action-buttons {
          display: flex;
          gap: 8px;
        }
        .btn-view {
          background: #6b7280;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-edit {
          background: white;
          border: 1px solid #d1d5db;
          color: #6b7280;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .reg-id {
          font-size: 0.75rem;
          color: #9ca3af;
          font-weight: 500;
        }
        .detail-modal-section {
          background: #ffffff;
          border-radius: 6px;
          padding: 14px 16px;
          margin-bottom: 12px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }
        .detail-modal-section h5 {
          color: #374151;
          font-weight: 600;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          font-size: 0.95rem;
          padding-bottom: 8px;
          border-bottom: 1px solid #f3f4f6;
        }
        .detail-modal-section h5 i {
          margin-right: 8px;
          background: #f3f4f6;
          color: #6b7280;
          padding: 5px;
          border-radius: 4px;
          font-size: 18px;
        }
        .detail-modal-section p {
          color: #4b5563;
          margin-bottom: 6px;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .detail-modal-section strong {
          color: #1f2937;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .registration-detail-modal-dialog {
          margin-top: 88px;
          margin-bottom: 24px;
        }
        .registration-detail-modal-dialog .modal-content {
          max-height: calc(100vh - 112px);
        }
        .registration-detail-modal-dialog .modal-body {
          max-height: calc(100vh - 210px);
          overflow-y: auto;
        }
        @media (max-width: 768px) {
          .registration-detail-modal-dialog {
            margin-top: 72px;
            margin-bottom: 16px;
          }
          .registration-detail-modal-dialog .modal-content {
            max-height: calc(100vh - 88px);
          }
          .registration-detail-modal-dialog .modal-body {
            max-height: calc(100vh - 180px);
          }
          .registration-card-header {
            padding: 16px;
          }
          .child-avatar {
            width: 50px;
            height: 50px;
          }
          .child-name {
            font-size: 1.1rem;
          }
          /* Row 345-399 below stacks the name/age/status block above this
             one on mobile, but .action-buttons (display:flex, gap:8px) plus
             the column's justify-content-end kept View/Edit pinned to the
             right edge of their own full-width row — floating on their own
             with empty space to the left instead of lining up with the
             left-aligned content above them. Stretching them to share the
             row 50/50 reads as a deliberate mobile action bar instead. */
          .action-buttons {
            justify-content: stretch !important;
            gap: 12px;
          }
          .action-buttons .btn-view,
          .action-buttons .btn-edit {
            flex: 1;
            justify-content: center;
            margin: 0;
          }
        }
      `}</style>
      <div className="registration-list">
        {registrations.map((record, index) => (
          <Card key={record.id || index} className="registration-card">
            <div className="registration-card-header">
              <Row className="align-items-center">
                <Col md={8}>
                  <div className="child-name-section">
                    <div className="child-avatar">
                      {getChildProfileImage(record) ? (
                        <img
                          src={getChildProfileImage(record)}
                          alt="Child profile"
                          className="child-avatar-image"
                        />
                      ) : (
                        <i className="material-symbols-outlined text-white" style={{ fontSize: '32px' }}>child_care</i>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <h4 className="child-name">
                          {record.child_first_name || 'N/A'} {record.child_last_name || ''}
                        </h4>
                        <span className="reg-id">#{String(record.id || index + 1).padStart(4, '0')}</span>
                      </div>
                      <div className="registration-meta">
                        <span className="meta-badge">
                          <i className="material-symbols-outlined" style={{ fontSize: '16px' }}>cake</i>
                          {record.child_age || calculateAge(record.child_dob) || 'N/A'} years
                        </span>
                        <span className={`meta-badge ${isChildRegistrationRecordFilled(record) ? 'active' : 'inactive'}`}>
                          <i className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                            {isChildRegistrationRecordFilled(record) ? 'check_circle' : 'cancel'}
                          </i>
                          {isChildRegistrationRecordFilled(record) ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={4} className="text-end mt-3 mt-md-0">
                  <div className="action-buttons justify-content-end">
                    <Button
                      className="btn-view"
                      onClick={() => handleView(record)}
                    >
                      <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>visibility</i>
                      View
                    </Button>
                    <Button
                      className="btn-edit"
                      onClick={() => handleEdit(record.id)}
                    >
                      <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>edit</i>
                      Edit
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
            <Card.Body style={{ padding: '24px' }}>
              <Row>
                <Col md={6}>
                  <div className="registration-info-section">
                    <div className="info-header">
                      <div className="info-icon">
                        <i className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>person</i>
                      </div>
                      <h6 className="info-title">Child Information</h6>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Date of Birth:</span>
                      <span className="info-value">{formatDate(record.child_dob)}</span>
                    </div>
                    {record.child_city && (
                      <div className="info-row">
                        <span className="info-label">City:</span>
                        <span className="info-value">{record.child_city}</span>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="registration-info-section">
                    <div className="info-header">
                      <div className="info-icon">
                        <i className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>family_restroom</i>
                      </div>
                      <h6 className="info-title">Parent/Guardian</h6>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{getParentName(record)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">{formatPhoneNumber(getParentPhone(record))}</span>
                    </div>
                  </div>
                </Col>
              </Row>
              {record.enrollment_date && (
                <Row>
                  <Col md={12}>
                    <div className="registration-info-section">
                      <div className="info-header">
                        <div className="info-icon">
                          <i className="material-symbols-outlined text-white" style={{ fontSize: '18px' }}>event</i>
                        </div>
                        <h6 className="info-title">Enrollment Date</h6>
                      </div>
                      <div className="info-row">
                        <span className="info-value" style={{ fontSize: '1rem' }}>{formatDate(record.enrollment_date)}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={handleCloseModal} size="xl" dialogClassName="registration-detail-modal-dialog">
        <Modal.Header closeButton style={{ background: '#ffffff', color: '#1f2937', borderBottom: '1px solid #e5e7eb' }}>
          <Modal.Title style={{ fontSize: '1.3rem', fontWeight: '600', color: '#1f2937' }}>
            <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle', fontSize: '24px', color: '#6b7280' }}>description</i>
            Child Registration Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#f8f9fa', padding: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedRecord && (
            <div>
              {/* Child Information */}
              <div className="detail-modal-section">
                <h5>
                  <i className="material-symbols-outlined">child_care</i>
                  Child Information
                </h5>
                <Row>
                  <Col md={6}>
                    <p><strong>Full Name:</strong> {selectedRecord.child_first_name || 'N/A'} {selectedRecord.child_last_name || ''}</p>
                    <p><strong>Date of Birth:</strong> {formatDate(selectedRecord.child_dob || selectedRecord.child_date_of_birth)}</p>
                    <p><strong>Age:</strong> {selectedRecord.child_age || calculateAge(selectedRecord.child_dob || selectedRecord.child_date_of_birth) || 'N/A'} years</p>
                  </Col>
                  <Col md={6}>
                    {(selectedRecord.user_details?.user_setting?.address || selectedRecord.child_home_address || selectedRecord.child_address || selectedRecord.child_city || selectedRecord.child_postal_code) && (
                      <>
                        <p><strong>Address:</strong> {selectedRecord.user_details?.user_setting?.address || selectedRecord.child_home_address || selectedRecord.child_address || 'N/A'}</p>
                        <p><strong>City:</strong> {selectedRecord.child_city || 'N/A'}</p>
                        <p><strong>Postal/ZIP Code:</strong> {selectedRecord.child_postal_code || 'N/A'}</p>
                      </>
                    )}
                  </Col>
                </Row>
              </div>

              {/* Parent/Guardian 1 (Mother) */}
              {(selectedRecord.mother_name || selectedRecord.parent1_first_name) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">person</i>
                    {selectedRecord.mother_name ? 'Mother' : 'Parent/Guardian 1'}
                  </h5>
                  <Row>
                    <Col md={6}>
                      <p><strong>Name:</strong> {selectedRecord.mother_name || `${selectedRecord.parent1_first_name || ''} ${selectedRecord.parent1_last_name || ''}`.trim()}</p>
                      <p><strong>Relation:</strong> {selectedRecord.parent1_relation || 'Mother'}</p>
                      <p><strong>Home Phone:</strong> {formatPhoneNumber(selectedRecord.mother_home_phone || selectedRecord.parent1_phone)}</p>
                      <p><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.mother_cell || selectedRecord.parent1_cell)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Email:</strong> {selectedRecord.mother_email || selectedRecord.parent1_email || 'N/A'}</p>
                      <p><strong>Address:</strong> {
                        selectedRecord.mother_home_address ||
                        selectedRecord.parent1_home_address ||
                        selectedRecord.parent1_address ||
                        selectedRecord.father_home_address ||
                        selectedRecord.parent2_home_address ||
                        selectedRecord.parent2_address ||
                        'N/A'
                      }</p>
                      <p><strong>City:</strong> {selectedRecord.mother_city || selectedRecord.parent1_city || 'N/A'}</p>
                      <p><strong>Postal/ZIP Code:</strong> {selectedRecord.mother_postal_code || selectedRecord.parent1_zip || 'N/A'}</p>
                      {(selectedRecord.mother_work_phone || selectedRecord.parent1_work_phone) && (
                        <p><strong>Work Phone:</strong> {formatPhoneNumber(selectedRecord.mother_work_phone || selectedRecord.parent1_work_phone)}</p>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {/* Parent/Guardian 2 (Father) */}
              {(selectedRecord.father_name || selectedRecord.parent2_first_name) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">person</i>
                    {selectedRecord.father_name ? 'Father' : 'Parent/Guardian 2'}
                  </h5>
                  <Row>
                    <Col md={6}>
                      <p><strong>Name:</strong> {selectedRecord.father_name || `${selectedRecord.parent2_first_name || ''} ${selectedRecord.parent2_last_name || ''}`.trim()}</p>
                      <p><strong>Relation:</strong> {selectedRecord.parent2_relation || 'Father'}</p>
                      <p><strong>Home Phone:</strong> {formatPhoneNumber(selectedRecord.father_home_phone || selectedRecord.parent2_phone)}</p>
                      <p><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.father_cell || selectedRecord.parent2_cell)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Email:</strong> {selectedRecord.father_email || selectedRecord.parent2_email || 'N/A'}</p>
                      {!selectedRecord.same_address && (selectedRecord.father_home_address || selectedRecord.parent2_address) && (
                        <>
                          <p><strong>Address:</strong> {selectedRecord.father_home_address || selectedRecord.parent2_address}</p>
                          <p><strong>City:</strong> {selectedRecord.father_city || selectedRecord.parent2_city}</p>
                          <p><strong>Postal/ZIP Code:</strong> {selectedRecord.father_postal_code || selectedRecord.parent2_zip}</p>
                        </>
                      )}
                      {selectedRecord.same_address && (
                        <p><em>Same address as {selectedRecord.mother_name ? 'Mother' : 'Parent/Guardian 1'}</em></p>
                      )}
                      {(selectedRecord.father_work_phone || selectedRecord.parent2_work_phone) && (
                        <p><strong>Work Phone:</strong> {formatPhoneNumber(selectedRecord.father_work_phone || selectedRecord.parent2_work_phone)}</p>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {/* Emergency Contact */}
              {selectedRecord.emergency_contact_name && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">emergency</i>
                    Emergency Contact
                  </h5>
                  <Row>
                    <Col md={6}>
                      <p><strong>Name:</strong> {selectedRecord.emergency_contact_name}</p>
                      <p><strong>Relation:</strong> {selectedRecord.emergency_contact_relationship || selectedRecord.emergency_contact_relation || 'N/A'}</p>
                      <p><strong>Home Phone:</strong> {formatPhoneNumber(selectedRecord.emergency_contact_phone)}</p>
                      <p><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.emergency_contact_cell)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Email:</strong> {selectedRecord.emergency_contact_email || 'N/A'}</p>
                      {(selectedRecord.emergency_contact_home_address || selectedRecord.emergency_contact_address) && (
                        <>
                          <p><strong>Home Address:</strong> {selectedRecord.emergency_contact_home_address || selectedRecord.emergency_contact_address}</p>
                          <p><strong>City:</strong> {selectedRecord.emergency_contact_city}</p>
                          <p><strong>Postal/ZIP Code:</strong> {selectedRecord.emergency_contact_postal_code}</p>
                        </>
                      )}
                      {selectedRecord.emergency_contact_work_phone && (
                        <p><strong>Work Phone:</strong> {formatPhoneNumber(selectedRecord.emergency_contact_work_phone)}</p>
                      )}
                      {selectedRecord.emergency_contact_work_address && (
                        <p><strong>Work Address:</strong> {selectedRecord.emergency_contact_work_address}</p>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              {/* Authorized Pickup Persons */}
              {(selectedRecord.pickup1_name || selectedRecord.pickup2_name || selectedRecord.pickup3_name || selectedRecord.pickup4_name ||
                selectedRecord.pickup_person1_name || selectedRecord.pickup_person2_name || selectedRecord.pickup_person3_name || selectedRecord.pickup_person4_name) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">person_check</i>
                    Authorized Pick-Up List
                  </h5>
                  {(selectedRecord.pickup1_name || selectedRecord.pickup_person1_name) && (
                    <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Name:</strong> {selectedRecord.pickup1_name || selectedRecord.pickup_person1_name}</p>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1"><strong>Phone:</strong> {formatPhoneNumber(selectedRecord.pickup1_phone || selectedRecord.pickup_person1_phone)}</p>
                          {(selectedRecord.pickup1_cell || selectedRecord.pickup_person1_cell) && (
                            <p className="mb-1"><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.pickup1_cell || selectedRecord.pickup_person1_cell)}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          {(selectedRecord.pickup1_address || selectedRecord.pickup_person1_address) && (
                            <>
                              <p className="mb-1"><strong>Address:</strong> {selectedRecord.pickup1_address || selectedRecord.pickup_person1_address}</p>
                              <p className="mb-1"><strong>City:</strong> {selectedRecord.pickup1_city || selectedRecord.pickup_person1_city} | <strong>Postal/ZIP Code:</strong> {selectedRecord.pickup1_zip || selectedRecord.pickup_person1_postal_code}</p>
                            </>
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                  {(selectedRecord.pickup2_name || selectedRecord.pickup_person2_name) && (
                    <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Name:</strong> {selectedRecord.pickup2_name || selectedRecord.pickup_person2_name}</p>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1"><strong>Phone:</strong> {formatPhoneNumber(selectedRecord.pickup2_phone || selectedRecord.pickup_person2_phone)}</p>
                          {(selectedRecord.pickup2_cell || selectedRecord.pickup_person2_cell) && (
                            <p className="mb-1"><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.pickup2_cell || selectedRecord.pickup_person2_cell)}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          {(selectedRecord.pickup2_address || selectedRecord.pickup_person2_address) && (
                            <>
                              <p className="mb-1"><strong>Address:</strong> {selectedRecord.pickup2_address || selectedRecord.pickup_person2_address}</p>
                              <p className="mb-1"><strong>City:</strong> {selectedRecord.pickup2_city || selectedRecord.pickup_person2_city} | <strong>Postal/ZIP Code:</strong> {selectedRecord.pickup2_zip || selectedRecord.pickup_person2_postal_code}</p>
                            </>
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                  {(selectedRecord.pickup3_name || selectedRecord.pickup_person3_name) && (
                    <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Name:</strong> {selectedRecord.pickup3_name || selectedRecord.pickup_person3_name}</p>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1"><strong>Phone:</strong> {formatPhoneNumber(selectedRecord.pickup3_phone || selectedRecord.pickup_person3_phone)}</p>
                          {(selectedRecord.pickup3_cell || selectedRecord.pickup_person3_cell) && (
                            <p className="mb-1"><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.pickup3_cell || selectedRecord.pickup_person3_cell)}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          {(selectedRecord.pickup3_address || selectedRecord.pickup_person3_address) && (
                            <>
                              <p className="mb-1"><strong>Address:</strong> {selectedRecord.pickup3_address || selectedRecord.pickup_person3_address}</p>
                              <p className="mb-1"><strong>City:</strong> {selectedRecord.pickup3_city || selectedRecord.pickup_person3_city} | <strong>Postal/ZIP Code:</strong> {selectedRecord.pickup3_zip || selectedRecord.pickup_person3_postal_code}</p>
                            </>
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                  {(selectedRecord.pickup4_name || selectedRecord.pickup_person4_name) && (
                    <div style={{ background: '#f9fafb', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Name:</strong> {selectedRecord.pickup4_name || selectedRecord.pickup_person4_name}</p>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1"><strong>Phone:</strong> {formatPhoneNumber(selectedRecord.pickup4_phone || selectedRecord.pickup_person4_phone)}</p>
                          {(selectedRecord.pickup4_cell || selectedRecord.pickup_person4_cell) && (
                            <p className="mb-1"><strong>Cell:</strong> {formatPhoneNumber(selectedRecord.pickup4_cell || selectedRecord.pickup_person4_cell)}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          {(selectedRecord.pickup4_address || selectedRecord.pickup_person4_address) && (
                            <>
                              <p className="mb-1"><strong>Address:</strong> {selectedRecord.pickup4_address || selectedRecord.pickup_person4_address}</p>
                              <p className="mb-1"><strong>City:</strong> {selectedRecord.pickup4_city || selectedRecord.pickup_person4_city} | <strong>Postal/ZIP Code:</strong> {selectedRecord.pickup4_zip || selectedRecord.pickup_person4_postal_code}</p>
                            </>
                          )}
                        </Col>
                      </Row>
                    </div>
                  )}
                </div>
              )}

              {/* Arrival and Departure */}
              {(selectedRecord.dropoff_time || selectedRecord.drop_off_time || selectedRecord.pickup_time || selectedRecord.pick_up_time || 
                selectedRecord.start_date || selectedRecord.withdrawal_date || selectedRecord.parent_guardian_signature || 
                selectedRecord.parent_signature || selectedRecord.executive_director_signature) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">schedule</i>
                    Arrival and Departure
                  </h5>
                  <Row>
                    <Col md={6}>
                      {(selectedRecord.dropoff_time || selectedRecord.drop_off_time) && (
                        <p><strong>Drop-off Time:</strong> {selectedRecord.dropoff_time || selectedRecord.drop_off_time}</p>
                      )}
                      {(selectedRecord.pickup_time || selectedRecord.pick_up_time) && (
                        <p><strong>Pick-up Time:</strong> {selectedRecord.pickup_time || selectedRecord.pick_up_time}</p>
                      )}
                    </Col>
                    <Col md={6}>
                      {selectedRecord.start_date && (
                        <p><strong>Start Date:</strong> {formatDate(selectedRecord.start_date)}</p>
                      )}
                      {selectedRecord.withdrawal_date && (
                        <p><strong>Withdrawal Date:</strong> {formatDate(selectedRecord.withdrawal_date)}</p>
                      )}
                    </Col>
                  </Row>
                  {(selectedRecord.parent_guardian_signature || selectedRecord.parent_signature || selectedRecord.executive_director_signature) && (
                    <div className="mt-3">
                      <Row>
                        {(selectedRecord.parent_guardian_signature || selectedRecord.parent_signature) && (
                          <Col md={6}>
                            <p className="mb-2"><strong>Parent/Guardian Signature:</strong></p>
                            <div className="border rounded p-2 bg-light" style={{ display: 'inline-block', maxWidth: '300px' }}>
                              <img 
                                src={selectedRecord.parent_guardian_signature || selectedRecord.parent_signature} 
                                alt="Parent/Guardian Signature" 
                                style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                              />
                            </div>
                          </Col>
                        )}
                        {selectedRecord.executive_director_signature && (
                          <Col md={6}>
                            <p className="mb-2"><strong>Executive Director Signature:</strong></p>
                            <div className="border rounded p-2 bg-light" style={{ display: 'inline-block', maxWidth: '300px' }}>
                              <img 
                                src={selectedRecord.executive_director_signature} 
                                alt="Executive Director Signature" 
                                style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                              />
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  )}
                </div>
              )}

              {/* Emergency Medical Authorization */}
              {(selectedRecord.family_physician_name || selectedRecord.pediatrician_name || selectedRecord.physician_phone_number || 
                selectedRecord.pediatrician_phone || selectedRecord.current_medications || selectedRecord.medications || 
                selectedRecord.allergies_health_info || selectedRecord.allergies || selectedRecord.emergency_auth_signature) && (
                <div className="detail-modal-section">
                <h5>
                  <i className="material-symbols-outlined">medical_services</i>
                  Emergency Medical Authorization
                </h5>
                <Row>
                  <Col md={6}>
                    <p><strong>Family Physician:</strong> {selectedRecord.family_physician_name || selectedRecord.pediatrician_name || 'N/A'}</p>
                    <p><strong>Physician Phone:</strong> {formatPhoneNumber(selectedRecord.physician_phone_number || selectedRecord.pediatrician_phone)}</p>
                    {selectedRecord.physician_address && (
                      <p><strong>Physician Address:</strong> {selectedRecord.physician_address}</p>
                    )}
                  </Col>
                  <Col md={6}>
                    <p><strong>Current Medications:</strong> {selectedRecord.current_medications || selectedRecord.medications || 'None reported'}</p>
                    <p><strong>Allergies/Health Info:</strong> {selectedRecord.allergies_health_info || selectedRecord.allergies || 'None reported'}</p>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {(selectedRecord.sleep_time_night || selectedRecord.sleep_time) && (
                      <p><strong>Sleep Time (Night):</strong> {selectedRecord.sleep_time_night || selectedRecord.sleep_time}</p>
                    )}
                    {selectedRecord.child_favourite_activity && (
                      <p><strong>Child's Favourite Activity:</strong> {selectedRecord.child_favourite_activity}</p>
                    )}
                  </Col>
                  <Col md={6}>
                    {(selectedRecord.wake_time_morning || selectedRecord.wake_time) && (
                      <p><strong>Wake Time (Morning):</strong> {selectedRecord.wake_time_morning || selectedRecord.wake_time}</p>
                    )}
                  </Col>
                </Row>
                {selectedRecord.emergency_auth_signature && (
                  <div className="mt-3">
                    <p className="mb-2"><strong>Emergency Authorization Signature:</strong></p>
                    <div className="border rounded p-2 bg-light" style={{ display: 'inline-block', maxWidth: '300px' }}>
                      <img 
                        src={selectedRecord.emergency_auth_signature} 
                        alt="Emergency Auth Signature" 
                        style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                      />
                    </div>
                    {selectedRecord.emergency_auth_date && (
                      <div className="mt-2">
                        <small className="text-muted">Date: {formatDate(selectedRecord.emergency_auth_date)}</small>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Child Information Page */}
              {(selectedRecord.agency_involvement || selectedRecord.child_related_agencies_involvement || 
                selectedRecord.vision_history || selectedRecord.vision_history_glasses || 
                selectedRecord.hearing_concerns || selectedRecord.hearing_concerns_audiologist || 
                selectedRecord.diagnoses || selectedRecord.diagnoses_adhd_delays || 
                selectedRecord.communicable_disease || selectedRecord.communicable_disease_history || 
                selectedRecord.eating_concerns || selectedRecord.eating_appetite_concerns || 
                selectedRecord.developmental_concerns || 
                selectedRecord.pets_at_home || 
                selectedRecord.siblings_info || selectedRecord.siblings_family_info || 
                selectedRecord.favourite_toy || selectedRecord.child_favourite_toy_activity) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">description</i>
                    Child Information Page
                  </h5>
                  {(selectedRecord.agency_involvement || selectedRecord.child_related_agencies_involvement) && (
                    <p><strong>Involvement with Child-Related Agencies:</strong><br />{selectedRecord.child_related_agencies_involvement || selectedRecord.agency_involvement}</p>
                  )}
                  {(selectedRecord.vision_history || selectedRecord.vision_history_glasses) && (
                    <p><strong>Vision History / Glasses Usage:</strong><br />{selectedRecord.vision_history_glasses || selectedRecord.vision_history}</p>
                  )}
                  {(selectedRecord.hearing_concerns || selectedRecord.hearing_concerns_audiologist) && (
                    <p><strong>Hearing Concerns / Audiologist Visits:</strong><br />{selectedRecord.hearing_concerns_audiologist || selectedRecord.hearing_concerns}</p>
                  )}
                  {(selectedRecord.diagnoses || selectedRecord.diagnoses_adhd_delays) && (
                    <p><strong>Any Diagnoses (e.g., ADHD, delays):</strong><br />{selectedRecord.diagnoses_adhd_delays || selectedRecord.diagnoses}</p>
                  )}
                  {(selectedRecord.communicable_disease || selectedRecord.communicable_disease_history) && (
                    <p><strong>Communicable Disease History:</strong><br />{selectedRecord.communicable_disease_history || selectedRecord.communicable_disease}</p>
                  )}
                  {(selectedRecord.eating_concerns || selectedRecord.eating_appetite_concerns) && (
                    <p><strong>Eating/Appetite Concerns:</strong><br />{selectedRecord.eating_appetite_concerns || selectedRecord.eating_concerns}</p>
                  )}
                  {selectedRecord.developmental_concerns && (
                    <p><strong>Developmental Concerns:</strong><br />{selectedRecord.developmental_concerns}</p>
                  )}
                  {selectedRecord.pets_at_home && (
                    <p><strong>Pets at Home:</strong><br />{selectedRecord.pets_at_home}</p>
                  )}
                  {(selectedRecord.siblings_info || selectedRecord.siblings_family_info) && (
                    <p><strong>Siblings / Family Info:</strong><br />{selectedRecord.siblings_family_info || selectedRecord.siblings_info}</p>
                  )}
                  {(selectedRecord.favourite_toy || selectedRecord.child_favourite_toy_activity) && (
                    <p><strong>Child's Favourite Toy or Activity:</strong><br />{selectedRecord.child_favourite_toy_activity || selectedRecord.favourite_toy}</p>
                  )}
                </div>
              )}
              
              {/* Media Release Waiver */}
              {(selectedRecord.media_release_agree !== undefined || selectedRecord.media_release_disagree !== undefined || 
                selectedRecord.media_release_signature || selectedRecord.media_release_signature_agree || selectedRecord.media_release_signature_disagree) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">photo_camera</i>
                    Media Release Waiver
                  </h5>
                  {(selectedRecord.media_release_agree === 1 || selectedRecord.media_release_agree === '1' || selectedRecord.media_release_agree === true) && (
                    <div style={{ background: '#ecfdf5', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #d1fae5' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Status:</strong> <span style={{ color: '#059669' }}>AGREED</span></p>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Child's Name:</strong> {selectedRecord.media_release_child_name_agree || selectedRecord.media_release_child_name}</p>
                      <p className="mb-0" style={{ fontSize: '0.875rem' }}><strong>Parent's Name:</strong> {selectedRecord.media_release_parent_name_agree || selectedRecord.media_release_parent_name}</p>
                    </div>
                  )}
                  {(selectedRecord.media_release_agree === 0 || selectedRecord.media_release_agree === '0' || selectedRecord.media_release_agree === false || selectedRecord.media_release_disagree) && (
                    <div style={{ background: '#fef2f2', padding: '10px 12px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #fecaca' }}>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Status:</strong> <span style={{ color: '#dc2626' }}>DISAGREED</span></p>
                      <p className="mb-1" style={{ fontSize: '0.875rem' }}><strong>Child's Name:</strong> {selectedRecord.media_release_child_name_disagree || selectedRecord.media_release_child_name}</p>
                      <p className="mb-0" style={{ fontSize: '0.875rem' }}><strong>Parent's Name:</strong> {selectedRecord.media_release_parent_name_disagree || selectedRecord.media_release_parent_name}</p>
                    </div>
                  )}
                  {(selectedRecord.media_release_signature || selectedRecord.media_release_signature_agree || selectedRecord.media_release_signature_disagree) && (
                    <div className="mt-3">
                      <p className="mb-2"><strong>Parent Signature:</strong></p>
                      <div className="border rounded p-2 bg-light" style={{ display: 'inline-block', maxWidth: '300px' }}>
                        <img 
                          src={selectedRecord.media_release_signature_agree || selectedRecord.media_release_signature_disagree || selectedRecord.media_release_signature} 
                          alt="Media Release Signature" 
                          style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                        />
                      </div>
                      {(selectedRecord.media_release_date || selectedRecord.media_release_date_agree || selectedRecord.media_release_date_disagree) && (
                        <div className="mt-2">
                          <small className="text-muted">Date: {formatDate(selectedRecord.media_release_date_agree || selectedRecord.media_release_date_disagree || selectedRecord.media_release_date)}</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Off-Site Permission */}
              {(selectedRecord.offsite_child_name || selectedRecord.offsite_permission_child_name || 
                selectedRecord.offsite_permission_date || selectedRecord.offsite_date || 
                selectedRecord.offsite_signature || selectedRecord.offsite_permission_signature || 
                selectedRecord.offsite_parent_name || selectedRecord.offsite_permission_parent_name) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">directions_walk</i>
                    Off-Site Permission Form
                  </h5>
                  <p><strong>Child's Name:</strong> {selectedRecord.offsite_permission_child_name || selectedRecord.offsite_child_name}</p>
                  <p><strong>Parent/Guardian Name:</strong> {selectedRecord.offsite_permission_parent_name || selectedRecord.offsite_parent_name}</p>
                  <p><em>Permission granted for supervised off-site walks around the neighbourhood.</em></p>
                  {(selectedRecord.offsite_signature || selectedRecord.offsite_permission_signature) && (
                    <div className="mt-3">
                      <p className="mb-2"><strong>Parent/Guardian Signature:</strong></p>
                      <div className="border rounded p-2 bg-light" style={{ display: 'inline-block', maxWidth: '300px' }}>
                        <img 
                          src={selectedRecord.offsite_permission_signature || selectedRecord.offsite_signature} 
                          alt="Off-Site Permission Signature" 
                          style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                        />
                      </div>
                      {(selectedRecord.offsite_permission_date || selectedRecord.offsite_date) && (
                        <div className="mt-2">
                          <small className="text-muted">Date: {formatDate(selectedRecord.offsite_permission_date || selectedRecord.offsite_date)}</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Contract Terms and Conditions */}
              {(selectedRecord.contract_initial_1 || selectedRecord.contract_initial_2 || selectedRecord.contract_initial_3 || 
                selectedRecord.contract_initial_4 || selectedRecord.contract_initial_5 || selectedRecord.contract_initial_6 || 
                selectedRecord.contract_initial_7 || selectedRecord.contract_initial_8 || selectedRecord.contract_initial_9 || 
                selectedRecord.contract_initial_10 || selectedRecord.contract_initial_11 || selectedRecord.contract_initial_12 || 
                selectedRecord.contract_initial_13 || selectedRecord.contract_parent_signature || selectedRecord.financial_signature) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">gavel</i>
                    Contract Terms and Conditions
                  </h5>
                  <p className="mb-3"><em>Parent/Guardian signature for each clause:</em></p>
                  {[
                    { clause: "I have reviewed and understood the Parent Policies Handbook (PPH).", num: 1 },
                    { clause: "I agree to comply with all policies, including health, safety, and ministry regulations.", num: 2 },
                    { clause: "I acknowledge that custody/visitation disputes require valid court documentation.", num: 3 },
                    { clause: "I understand that while parental concerns are welcomed, certain policies are not subject to parental discretion.", num: 4 },
                    { clause: "All required enrollment forms and documents will be submitted before the child's start date.", num: 5 },
                    { clause: "I agree that tuition is due on the 1st of every month, and non-payment may result in suspended attendance.", num: 6 },
                    { clause: "I grant permission for emergency medical care for my child if necessary.", num: 7 },
                    { clause: "I agree to pay all extra-curricular activity fees in advance.", num: 8 },
                    { clause: "I understand that 30 days' written notice is required for any withdrawal or schedule change.", num: 9 },
                    { clause: "I understand that fees are non-refundable for absences, illness, or statutory holidays.", num: 10 },
                    { clause: "I acknowledge that enrolment may be terminated for delinquent accounts, and I am responsible for any associated collection or legal fees.", num: 11 },
                    { clause: "I am responsible for ensuring that all authorized persons follow pick-up procedures.", num: 12 },
                    { clause: "I understand non-compliance with these terms may result in contract termination.", num: 13 }
                  ].map((item) => {
                    const initial = selectedRecord[`contract_initial_${item.num}`] || selectedRecord[`contract_clause${item.num}_initial`];
                    // Check both signature field and clause initial field for signature URLs
                    const signature = selectedRecord[`contract_signature_${item.num}`] || 
                                    (typeof selectedRecord[`contract_clause${item.num}_initial`] === 'string' && 
                                     selectedRecord[`contract_clause${item.num}_initial`].includes('http') ? 
                                     selectedRecord[`contract_clause${item.num}_initial`] : null);
                    
                    if (!initial && !signature) return null;
                    
                    return (
                      <div key={item.num} className="mb-2" style={{ background: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb', padding: '10px 12px' }}>
                        <Row className="align-items-center">
                          <Col xs={12} md={signature ? 8 : 12}>
                            <p className="mb-1" style={{ fontSize: '0.875rem', fontWeight: '500', lineHeight: '1.5' }}>
                              {item.num}. {item.clause}
                            </p>
                            {!signature && (
                              <p className="mb-0 mt-1" style={{ fontSize: '0.8rem', color: '#10b981' }}>
                                <i className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>check_circle</i>
                                {' '}Acknowledged
                              </p>
                            )}
                          </Col>
                          {signature && (
                            <Col xs={12} md={4} className="text-md-end mt-2 mt-md-0">
                              <div style={{ display: 'inline-block', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff' }}>
                                <img 
                                  src={signature} 
                                  alt={`Clause ${item.num} Signature`} 
                                  style={{ maxHeight: '50px', maxWidth: '120px', width: 'auto', display: 'block' }}
                                />
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    );
                  })}
                  
                  <Row className="mt-3">
                    <Col md={6}>
                      <p><strong>Parent/Guardian Name:</strong> {selectedRecord.contract_parent_name}</p>
                    </Col>
                    <Col md={6}>
                      {selectedRecord.contract_parent_date && (
                        <p><strong>Date:</strong> {formatDate(selectedRecord.contract_parent_date)}</p>
                      )}
                    </Col>
                  </Row>
                  {(selectedRecord.financial_party_name || selectedRecord.financial_agency) && (
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '6px', marginTop: '12px' }}>
                      <p className="mb-1"><strong>Financially Responsible Party</strong></p>
                      <p className="mb-1"><strong>Name:</strong> {selectedRecord.financial_party_name}</p>
                      {selectedRecord.financial_agency && (
                        <p className="mb-1"><strong>Agency:</strong> {selectedRecord.financial_agency}</p>
                      )}
                      {selectedRecord.financial_party_date && (
                        <p className="mb-0"><strong>Date:</strong> {formatDate(selectedRecord.financial_party_date)}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Contract Signatures */}
                  <Row className="mt-4">
                    {selectedRecord.contract_parent_signature && (
                      <Col md={6}>
                        <div style={{ marginBottom: '16px' }}>
                          <p className="mb-2"><strong>Parent/Guardian Signature:</strong></p>
                          <div style={{ display: 'inline-block', maxWidth: '300px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff' }}>
                            <img 
                              src={selectedRecord.contract_parent_signature} 
                              alt="Parent/Guardian Signature" 
                              style={{ maxHeight: '120px', width: 'auto', display: 'block' }}
                            />
                          </div>
                          {selectedRecord.contract_parent_date && (
                            <p className="mt-2 mb-0" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                              <strong>Date:</strong> {formatDate(selectedRecord.contract_parent_date)}
                            </p>
                          )}
                        </div>
                      </Col>
                    )}
                    {selectedRecord.financial_signature && (
                      <Col md={6}>
                        <div style={{ marginBottom: '16px' }}>
                          <p className="mb-2"><strong>Financial Party Signature:</strong></p>
                          <div style={{ display: 'inline-block', maxWidth: '300px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff' }}>
                            <img 
                              src={selectedRecord.financial_signature} 
                              alt="Financial Party Signature" 
                              style={{ maxHeight: '120px', width: 'auto', display: 'block' }}
                            />
                          </div>
                          {selectedRecord.financial_party_date && (
                            <p className="mt-2 mb-0" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                              <strong>Date:</strong> {formatDate(selectedRecord.financial_party_date)}
                            </p>
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {/* Registration Checklist Summary */}
              {(selectedRecord.checklist_registration_form || selectedRecord.checklist_emergency_form || selectedRecord.checklist_health_forms || 
                selectedRecord.checklist_birth_certificate || selectedRecord.checklist_parent_handbook || selectedRecord.checklist_waivers || 
                selectedRecord.checklist_child_info_page || selectedRecord.checklist_signed_contract || selectedRecord.checklist_offsite_form || 
                selectedRecord.checklist_medical_reports || selectedRecord.checklist_pad_form || selectedRecord.checklist_all_about_me || 
                selectedRecord.checklist_meal_waiver || selectedRecord.parent_checklist_signature) && (
                <div className="detail-modal-section">
                  <h5>
                    <i className="material-symbols-outlined">checklist</i>
                    Registration Checklist Summary
                  </h5>
                  <p className="mb-3"><em>Parent confirmation of submitted documents:</em></p>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_registration_form} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Registration Form
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_emergency_form} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Emergency Form
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_health_forms} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Health Forms (Immunization)
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_birth_certificate} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Birth Certificate
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_parent_handbook} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Read and understood Parent Handbook
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_waivers} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Waivers
                        </label>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_child_info_page} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Child Information Page
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_signed_contract} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Signed Contract
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_offsite_form} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Off-Site Permission Form
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_medical_reports} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Relevant medical/assessment reports (if any)
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_pad_form} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Pre-Authorized Debit Form
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_all_about_me} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          "All About Me" Form
                        </label>
                      </div>
                      <div className="mb-2">
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.95rem' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedRecord.checklist_meal_waiver} 
                            disabled 
                            style={{ marginRight: '8px', transform: 'scale(1.2)' }}
                          />
                          Meal Waiver (if applicable)
                        </label>
                      </div>
                    </Col>
                  </Row>
                  {selectedRecord.checklist_parent_date && (
                    <p className="mt-3"><strong>Date:</strong> {formatDate(selectedRecord.checklist_parent_date)}</p>
                  )}
                  
                  {/* Checklist Signatures */}
                  <Row className="mt-4">
                    {(selectedRecord.parent_checklist_signature || selectedRecord.checklist_parent_signature) && (
                      <Col md={6}>
                        <div className="mb-3">
                          <p className="mb-2"><strong>Parent/Guardian Signature:</strong></p>
                          <div style={{ display: 'inline-block', maxWidth: '300px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff' }}>
                            <img 
                              src={selectedRecord.parent_checklist_signature || selectedRecord.checklist_parent_signature} 
                              alt="Parent Checklist Signature" 
                              style={{ maxHeight: '120px', width: 'auto', display: 'block' }}
                            />
                          </div>
                          {selectedRecord.checklist_parent_date && (
                            <p className="mt-2 mb-0" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                              <strong>Date:</strong> {formatDate(selectedRecord.checklist_parent_date)}
                            </p>
                          )}
                        </div>
                      </Col>
                    )}
                    {(selectedRecord.staff_witness_signature || selectedRecord.checklist_staff_signature) && (
                      <Col md={6}>
                        <div className="mb-3">
                          <p className="mb-2"><strong>Staff Witness Signature:</strong></p>
                          <div style={{ display: 'inline-block', maxWidth: '300px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#fff' }}>
                            <img 
                              src={selectedRecord.staff_witness_signature || selectedRecord.checklist_staff_signature} 
                              alt="Staff Witness Signature" 
                              style={{ maxHeight: '120px', width: 'auto', display: 'block' }}
                            />
                          </div>
                          {selectedRecord.checklist_staff_date && (
                            <p className="mt-2 mb-0" style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                              <strong>Date:</strong> {formatDate(selectedRecord.checklist_staff_date)}
                            </p>
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

              {/* Uploaded Documents - Health Forms / Medical Reports / Birth
                  Certificate. Backend field names are health_form_document /
                  medical_report_document / birth_certificate_document; the
                  health_immunization_document / medical_reports_document
                  fallbacks are only for a record saved under the older
                  field name (see ChildRegistrationFormComp.js's apiPayload).
                  Each only shows up here once the parent has actually
                  uploaded it. */}
              {(() => {
                const documents = [
                  { label: 'Health Forms (Immunization)', url: selectedRecord.health_form_document || selectedRecord.health_immunization_document },
                  { label: 'Relevant Medical/Assessment Reports', url: selectedRecord.medical_report_document || selectedRecord.medical_reports_document },
                  { label: 'Birth Certificate', url: selectedRecord.birth_certificate_document }
                ].filter((doc) => doc.url);

                if (documents.length === 0) return null;

                return (
                  <div className="detail-modal-section">
                    <h5>
                      <i className="material-symbols-outlined">folder_open</i>
                      Uploaded Documents
                    </h5>
                    <Row>
                      {documents.map((doc, idx) => {
                        const isPdf = doc.url.toLowerCase().endsWith('.pdf');
                        return (
                          <Col md={4} key={idx} className="mb-3">
                            <div className="border rounded p-3" style={{ background: '#f9fafb' }}>
                              <p className="mb-2" style={{ fontSize: '0.875rem', fontWeight: 600 }}>{doc.label}</p>
                              {isPdf ? (
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <i className="material-symbols-outlined" style={{ fontSize: '18px' }}>picture_as_pdf</i>
                                  View PDF
                                </a>
                              ) : (
                                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={doc.url}
                                    alt={doc.label}
                                    style={{ maxWidth: '100%', maxHeight: '140px', display: 'block', borderRadius: '4px' }}
                                  />
                                </a>
                              )}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })()}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
          >
            <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>close</i>
            Close
          </Button>
          {viewMode !== 'profile' && selectedRecord && (
            <Button
              variant="primary"
              onClick={() => {
                handleCloseModal();
                handleEdit(selectedRecord.id);
              }}
            >
              <i className="material-symbols-outlined me-1" style={{ fontSize: '18px', verticalAlign: 'middle' }}>edit</i>
              Edit Registration
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChildRegistrationListComp;
